/**
 * Word boundaries in text, from Intl.Segmenter: it finds the words of
 * languages written without spaces (Chinese, Japanese, Thai), which a regular
 * expression can't. Shared by the Web Reader (rangeTranslator), which finds
 * words on demand, and the reader page, which groups its per-character spans
 * into words (readerText/tokens).
 */

import { cjkThai } from "shared/unicodePatterns";
import * as spanUtils from "spanUtils";

export type Segment = { segment: string; index: number; isWordLike: boolean };

const segmenters: Record<string, any> = {};

/**
 * Browsers without Intl.Segmenter (Firefox before 125): runs of letters, but
 * single characters in languages written without spaces, as spannify does
 */
const segmentWithRegExp = (text: string): Segment[] => {
  const characterRegExp = new RegExp(cjkThai, "u");
  const segments: Segment[] = [];
  const wordRegExp = spanUtils.wordRegExp();
  for (
    let match = wordRegExp.exec(text);
    match;
    match = wordRegExp.exec(text)
  ) {
    if (characterRegExp.test(match[0])) {
      let index = match.index!;
      for (const character of match[0]) {
        segments.push({ segment: character, index, isWordLike: true });
        index += character.length;
      }
    } else {
      segments.push({
        segment: match[0],
        index: match.index!,
        isWordLike: true,
      });
    }
  }
  return segments;
};

export const segment = (text: string, language: string): Iterable<Segment> => {
  const Segmenter = (Intl as any).Segmenter;
  if (!Segmenter) {
    return segmentWithRegExp(text);
  }
  segmenters[language] ??= new Segmenter(language, { granularity: "word" });
  return segmenters[language].segment(text);
};

/** Exposed for unit tests only */
export const _functionsForUnitTests = { segmentWithRegExp };
